import { QRCodeSVG } from 'qrcode.react'
import type { CSSProperties } from 'react'
import { cn } from '~/shared/lib/utils'
import { type FlyerContent, WORDMARK_SRC } from './flyer-content'

export function CheckinFlyer({
  content,
  className,
  style,
}: {
  content: FlyerContent
  className?: string
  style?: CSSProperties
}) {
  return (
    <div
      style={style}
      className={cn(
        '@container relative flex flex-col overflow-hidden bg-flyer-night text-flyer-paper [-webkit-print-color-adjust:exact] [print-color-adjust:exact]',
        className,
      )}
    >
      {content.motif === 'ahead' || content.motif === 'promo' ? (
        <AheadLayout content={content} />
      ) : (
        <HereLayout content={content} />
      )}
    </div>
  )
}

function Cover({ content, className }: { content: FlyerContent; className: string }) {
  return (
    <div className={cn('relative w-full shrink-0', className)}>
      {content.coverUrl ? (
        <img src={content.coverUrl} alt="" className="absolute inset-0 size-full object-cover" />
      ) : (
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_15%,var(--flyer-accent),transparent_65%)] opacity-45" />
      )}
      <div className="absolute inset-0 bg-linear-to-b from-flyer-night/70 via-flyer-night/5 via-30% to-flyer-night" />
      <img
        src={WORDMARK_SRC}
        alt=""
        className="absolute top-[7cqw] left-[7cqw] h-[5.5cqw] w-auto"
      />
    </div>
  )
}

function Qr({ url, className }: { url: string; className: string }) {
  return (
    <div className="rounded-[3cqw] bg-flyer-paper p-[2.4cqw]">
      <QRCodeSVG value={url} marginSize={0} className={cn('block', className)} />
    </div>
  )
}

function AheadLayout({ content }: { content: FlyerContent }) {
  return (
    <>
      <div className="relative">
        <Cover content={content} className="h-[72cqw]" />
        <div className="absolute inset-x-[7cqw] bottom-[2cqw] flex flex-col gap-[1.6cqw]">
          <h1 className="line-clamp-3 font-serif text-[8.5cqw] leading-[1.05] font-semibold">
            {content.title}
          </h1>
          <p className="text-[3.2cqw] font-medium">{content.when}</p>
          {content.venue && <p className="text-[3.2cqw] text-flyer-paper/75">{content.venue}</p>}
        </div>
      </div>
      <div className="flex flex-1 items-end gap-[5cqw] px-[7cqw] pt-[5cqw] pb-[7cqw]">
        <div className="flex min-w-0 flex-1 flex-col gap-[2.6cqw] self-start">
          <span className="h-[0.8cqw] w-[10cqw] rounded-full bg-flyer-accent" />
          <h2 className="font-serif text-[6.4cqw] leading-[1.08] font-semibold">
            {content.headline}
          </h2>
          <p className="text-[3cqw] leading-snug text-flyer-paper/80">{content.tagline}</p>
          <p className="text-[2.4cqw] font-semibold tracking-[0.14em] text-flyer-accent uppercase">
            {content.fineprint}
          </p>
        </div>
        <Qr url={content.url} className="size-[44cqw]" />
      </div>
    </>
  )
}

function HereLayout({ content }: { content: FlyerContent }) {
  return (
    <>
      <div className="relative">
        <Cover content={content} className="h-[26cqw]" />
        <p className="absolute inset-x-[7cqw] bottom-[1cqw] line-clamp-1 font-semibold text-[3.6cqw] text-flyer-paper/85">
          {content.title}
        </p>
      </div>
      <div className="flex flex-1 flex-col items-center px-[7cqw] pt-[3cqw] pb-[5cqw] text-center">
        <h2 className="font-serif text-[9cqw] leading-[1.02] font-semibold">{content.headline}</h2>
        <p className="mt-[2.4cqw] max-w-[80cqw] text-[3.4cqw] leading-snug text-flyer-paper/85">
          {content.tagline}
        </p>
        <div className="mt-auto flex flex-col items-center gap-[2.6cqw] pt-[4cqw]">
          <Qr url={content.url} className="size-[54cqw]" />
          <p className="text-[2.8cqw] font-semibold tracking-[0.16em] text-flyer-accent uppercase">
            {content.fineprint}
          </p>
          {content.code && (
            <p className="font-mono text-[3.4cqw] tracking-[0.12em] text-flyer-paper/70">
              {content.codeLabel} {content.code}
            </p>
          )}
        </div>
      </div>
    </>
  )
}
